import { useCallback, useContext } from 'react';
import dataRoomApi from '../dataRoomApi';
import confirm from 'modules/confirm';
import { useTranslation } from 'react-i18next';
import { DataRoomMember, DataRoomMembers } from '../models/DataRoomMember';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import DataRoomContext from '../providers/DataRoom/DataRoom.context';
import { getUserFullNameOrEmail } from 'utils';

type DeleteMemberOptions = {
  member: Partial<
    Pick<DataRoomMember, '_id' | 'firstName' | 'lastName' | 'email'>
  >;
  // Only buy-side groups have a name to show; elsewhere we name the data room instead.
  groupName?: string;
  setIsLoading?: (value: boolean) => void;
  onSuccess?: (data: DataRoomMembers) => void;
};

export default function useDeleteMember() {
  const { popServerError } = useContext(PopNotificationsContext);
  const { dataRoom } = useContext(DataRoomContext);

  const { t } = useTranslation();

  return useCallback(
    async (options: DeleteMemberOptions) => {
      const { member, groupName, setIsLoading, onSuccess } = options;

      const memberName = getUserFullNameOrEmail(member) || t('thisMember');

      const content = groupName
        ? t('removeMemberFromGroupMessage', { memberName, groupName })
        : t('removeMemberFromDataRoomMessage', {
            memberName,
            dataRoomName: dataRoom?.name,
          });

      if (
        await confirm({
          title: t('removeMember'),
          content,
          submitBtnText: t('removeMember'),
        })
      ) {
        try {
          setIsLoading?.(true);
          const { data } = await dataRoomApi.members.removeMember(member?._id);
          onSuccess?.(data);
        } catch (error) {
          popServerError(error);
        } finally {
          setIsLoading?.(false);
        }
      }
    },
    [t, dataRoom?.name, popServerError],
  );
}
